import type { CandidateRejectionReason } from "../../../common/types/CandidateRejectionReason";
// QUERIES
import { LocalDate } from "../../../common/types/commonTypes";
import { TempEmploymentReasonCode } from "../../../common/types/TempEmploymentReasonCode";

export interface CertificateDocumentUploadUrlQuery {
  certificateId: string;
  filename: string;
  size: string;
  talentId: string;
}

export interface TalentHrRepresentativesAutocompleteFilters {
  search: string;
  enabled?: boolean;
}

// QUERY PARAMETERS
export interface TalentIdQueryParameter {
  talentId: string;
}

export interface CertificateDocumentDownloadUrlQuery {
  pathspec: string;
  talentId?: string;
}

// QUERY RESULTS
export interface DocumentUploadUrl {
  url: string;
}

export interface DocumentDownloadUrl {
  url: string;
}

export interface CvDocumentUploadUrlQuery {
  size: string;
  filename: string;
  talentId: string;
}

export interface TalentProfilePictureDownloadUrlQuery {
  pathspec: string;
  talentId: string;
}

export interface TalentProfilePictureDownloadUrl {
  url: string;
}

export interface TalentHrRepresentativeAutocomplete {
  id: string;
  firstName: string;
  lastName: string;
}

export interface AssignmentsByTalentIdFilters {
  talentId: string;
  start?: string;
  end?: string;
}

export enum TalentIntroducedToCustomerStatus {
  Introduced = "INTRODUCED",
  Interview = "INTERVIEW",
  Approved = "APPROVED",
  Offered = "OFFERED",
  Accepted = "ACCEPTED",
  EezyRejected = "EEZY_REJECTED",
  Withdrew = "WITHDREW",
  Cancelled = "CANCELLED",
  Rejected = "REJECTED",
}

export const ACTIVE_WITHOUT_ORDER_INTRODUCTION_STATUSES =
  TalentIntroducedToCustomerStatus.Introduced;

export const ACTIVE_WITH_ORDER_INTRODUCTION_STATUSES = [
  TalentIntroducedToCustomerStatus.Introduced,
  TalentIntroducedToCustomerStatus.Interview,
  TalentIntroducedToCustomerStatus.Approved,
  TalentIntroducedToCustomerStatus.Offered,
].join(",");

export const ALL_WITHOUT_ORDER_INTRODUCTION_STATUSES = [
  TalentIntroducedToCustomerStatus.Introduced,
  TalentIntroducedToCustomerStatus.Approved,
  TalentIntroducedToCustomerStatus.Rejected,
  TalentIntroducedToCustomerStatus.Cancelled,
].join(",");

export const ALL_WITH_ORDER_INTRODUCTION_STATUSES = Object.values(
  TalentIntroducedToCustomerStatus,
).join(",");

export interface TalentIntroductionOrderHandler {
  id?: string;
  name: string;
}

export interface OverlappableAssignmentsByTalentIdFilters {
  bundleId: string;
  talentIds: string[];
}

export interface TalentIntroduction {
  customerOrgId: string;
  customerOrgName: string;
  workLocationId: string;
  workLocationName: string;
  talentId: string;
  introducedAt: EpochTimeStamp;
  workTitleIds: string[];
  status: TalentIntroducedToCustomerStatus;
  lastStatusUpdatedAt?: EpochTimeStamp | null;
  lastStatusUpdatedBy?: string | null;
  bundleId: string | null;
  orderTitle?: string | null;
  workStarts?: EpochTimeStamp | null;
  workEnds?: EpochTimeStamp | null;
  orderHandlers?: TalentIntroductionOrderHandler[];
  rejectionReason?: CandidateRejectionReason | null;
  rejectionComment?: string | null;
}

export interface CandidateIntroductionsByTalentIdFilters {
  talentId: string;
  pageSize: string;
  types?: "WithoutOrder" | "WithOrder";
  statuses?: string;
  start?: string;
  cursor?: string;
}

export interface TalentSelfServiceOrderPermission {
  id: string;
  talentId: string;
  version: number;
  workLocationId: string;
  workLocationName: string;
  isWorkLocationActive: boolean;
  customerOrganizationId: string;
  customerOrganizationName: string;
  customerCostCenterId: string;
  customerCostCenterName: string;
  isCustomerCostCenterActive: boolean;
  orderRowTemplateId: string;
  orderRowTemplateName: string;
  isOrderRowTemplateActive: boolean;
  reasonCode: TempEmploymentReasonCode;
  permissionStart: LocalDate;
  permissionEnd: LocalDate;
  workTitleId: string;
  tesId: string;
}

export interface TalentSelfServiceOrderPermissionsResponse {
  permissions: TalentSelfServiceOrderPermission[];
}

export interface CertificateDefinitionsFilters {
  term?: string;
  language: string;
}

export interface CertificateDefinition {
  id: string;
  name: string;
  requiresDocument: boolean;
}

export interface TrainingDefinitionsFilters {
  customerCompanyId?: string;
}

export interface TrainingDefinition {
  id: string;
  localizedName: {
    fi?: string;
    en?: string;
    sv?: string;
  };
  customerCompanyId?: string;
}

export interface TalentCvParams {
  pathspec: string;
  talentId: string;
}

export interface TalentLogEvent {
  name: string;
  time: EpochTimeStamp;
  userId: string;
  payload: any;
}

export interface ExclusivitiesByTalentIdFilters {
  talentId: string;
  pageSize?: number;
  cursor?: string;
}
